import { useParams } from 'react-router-dom'
import { Table, useApiQuery, type Column, type UsageFee } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { formatNumberN } from './paymentFlow'
import { PaymentPage } from './paymentShared'
import { failureText } from '../../api/errorMessages'

function consumablePlanIds(fee: UsageFee): string {
  return (fee.consumablePlans ?? [])
    .map((p) => (typeof p === 'string' ? p : (p.planId ?? '')))
    .filter(Boolean)
    .join(', ')
}

/**
 * `/payment/usagefees[/:usagefeeId]` — legacy `UsagefeeListComponent`,
 * read-only: the per-unit usage fees (`GET /payment/usagefees`). The legacy
 * add / edit dialogs are not ported: api.grabbitbot.com has no write route
 * for usage fees (they are managed in the payment service), and there is
 * no single-fee route either — `:usagefeeId` highlights that row.
 */
export function UsageFeeListPage() {
  const { usagefeeId } = useParams<{ usagefeeId?: string }>()
  const { data, loading, error } = useApiQuery(() => paymentClient.listUsageFees(), [])
  const rows = Array.isArray(data) ? data : []
  const columns: Column<UsageFee>[] = [
    { key: 'name', label: 'Name', align: 'left', sortValue: (r) => r.name ?? '' },
    {
      key: 'price',
      label: 'Price',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.price ?? 0,
      format: (_v, r) => `${formatNumberN(r.price, 8)} ${r.currency ?? ''}`.trim(),
    },
    {
      key: 'unit',
      label: 'Per',
      align: 'right',
      numeric: true,
      sortValue: (r) => r.unit ?? 0,
      format: (_v, r) => `${r.unit ?? ''} ${r.unitName ?? ''}`.trim(),
    },
    { key: 'fixUnits', label: 'Fixed units', align: 'left', sortValue: (r) => (r.fixUnits ? 1 : 0), format: (_v, r) => (r.fixUnits ? 'yes' : 'no') },
    { key: 'consumablePlans', label: 'Included in plans', align: 'left', sortValue: (r) => consumablePlanIds(r), format: (_v, r) => consumablePlanIds(r) },
  ]
  return (
    <PaymentPage title="Usage fees">
      {loading && <p>Loading usage fees…</p>}
      {error && (
        <p className="portal-notice portal-notice--error" role="alert">
          {failureText(error, 'Could not load usage fees')}
        </p>
      )}
      {!loading && !error && rows.length === 0 && <p className="portal-text-muted">No usage fees.</p>}
      {rows.length > 0 && (
        <section className="portal-card">
          <div className="portal-table-scroll">
            <Table
              columns={columns}
              rows={rows}
              rowKey={(r, i) => r.id ?? i}
              rowClassName={(r) => (usagefeeId && r.id === usagefeeId ? 'portal-row--selected' : undefined)}
            />
          </div>
        </section>
      )}
    </PaymentPage>
  )
}
